import { useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { DateTime } from "luxon";
import { H2, H3 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Button } from "../../../components/Button";
import { Box } from "../../../components/Box";
import { Card } from "../../../components/Card";
import { Flex } from "../../../components/Flex";
import { Image } from "../../../components/Image";
import { Modal } from "../../../components/Modal";
import { ROUTE } from "../../../routes";
import { useRunChecks } from "../../../data/useRunChecks";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { CHECK_ICONS } from "../../../utils/screeningChecks";
import { NewProfileScreeningFrame } from "./NewProfileScreeningFrame";
import { capitalise } from "../../../utils/string";

const Header = () => (
  <>
    <H2>Run screening</H2>
  </>
);

const CardLine = ({ children, ...styleProps }: any) => (
  <Paragraph fontSize={1} color="gray.60" my={0} {...styleProps}>
    {children}
  </Paragraph>
);

const formatAddressLines = (a: any) => {
  if (!a) return { line1: "-", line2: "-", postcode: "-", country: "-" };

  const line1 = [
    a?.flat_appartment_subbuilding,
    a?.building_number,
    a?.building_name,
    a?.road_street,
  ]
    .filter(Boolean)
    .join(" ");

  const line2 = [a?.town_city, a?.state_province_name || a?.district]
    .filter(Boolean)
    .join(", ");

  return {
    line1: line1 || "-",
    line2: line2 || "-",
    postcode: a?.post_zip_code ?? "-",
    country: a?.country_name ?? a?.country ?? "-",
    country_flag: a?.country_flag,
  };
};

export const ScreeningReviewStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<any>();
  const state: any = location.state ?? {};

  const isEntity = profile?._type === "entity";
  const mode = isEntity ? "entity" : "person";
  const [showSuccess, setShowSuccess] = useState(false);
  const [doubleClick, setDoubleClick] = useState(false);

  const { mutate: runChecks } = useRunChecks();
  const { data: entityTypes } = useEntityTypes();

  const entityTypeName = useMemo(() => {
    const et = profile?.entity_type;
    // The API returns entity_type either as an object { id, name } or a bare id.
    if (et && typeof et === "object" && (et as any).name) return (et as any).name;
    const list = Array.isArray(entityTypes)
      ? entityTypes
      : (entityTypes as any)?.data ?? [];
    const id = typeof et === "number" ? et : (et as any)?.id;
    return list.find((x: any) => x.id === id)?.name ?? "-";
  }, [entityTypes, profile?.entity_type]);

  const runTargetId = Number(state?.createdRecordId ?? profileId);

  const selectedChecks: any[] = Array.isArray(state?.selectedChecks)
    ? state.selectedChecks
    : [];

  const checksIcons: Record<string, any> = CHECK_ICONS;

  const displayName = useMemo(() => {
    if (state?.screeningSubjectName) return state.screeningSubjectName;
    if (isEntity) return profile?.name ?? "-";
    return (
      [profile?.first_name, profile?.middle_name, profile?.last_name]
        .filter(Boolean)
        .join(" ") || "-"
    );
  }, [isEntity, profile, state?.screeningSubjectName]);

  const registeredAddress = useMemo(() => {
    const addresses = profile?.address ?? [];

    if (isEntity) {
      return (
        addresses.find((a: any) => a?._type?.name === "Registered") ||
        addresses[0] ||
        null
      );
    }

    return (
      addresses.find((a: any) => a?._type?.name === "Primary Residence") ||
      addresses.find((a: any) => a?._type?.name === "Residential") ||
      addresses[0] ||
      null
    );
  }, [profile?.address, isEntity]);

  const address = formatAddressLines(registeredAddress);

  const handleRun = () => {
    setDoubleClick(true);

    runChecks(
      { id: runTargetId },
      {
        onSuccess: () => setShowSuccess(true),
        onError: () => {
          setDoubleClick(false);
        },
      },
    );
  };

  const goToProfile = () => {
    setShowSuccess(false);
    navigate(
      ROUTE.PROFILE_DETAIL_VIEW.replace(":id", profileId.toString()) +
        "?tab=screening",
    );
  };

  return (
    <NewProfileScreeningFrame step="review" mode={mode}>
      <Header />

      <Box mt={2} minWidth={500}>
        {/* Profile card */}
        <Card mb={2}>
          <H3 fontSize={2}>{displayName}</H3>

          {isEntity ? (
            <>
              {entityTypeName !== "-" ? (
                <CardLine my={1}>{entityTypeName}</CardLine>
              ) : (
                <>
                  <Paragraph fontSize="0" mt={1} mb={0}>
                    Entity type
                  </Paragraph>
                  <CardLine mb={2}>-</CardLine>
                </>
              )}

              {profile?.registration_number ? (
                <CardLine>{profile.registration_number}</CardLine>
              ) : (
                <>
                  <Paragraph fontSize="0" mb={0}>
                    Registration number
                  </Paragraph>
                  <CardLine>-</CardLine>
                </>
              )}

              {profile?.date_of_incorporation && (
                <CardLine my={1}>
                  {DateTime.fromISO(profile.date_of_incorporation).toFormat(
                    "dd/MM/yyyy",
                  )}
                </CardLine>
              )}
            </>
          ) : (
            <>
              {profile?.date_of_birth && (
                <CardLine my={1}>
                  {DateTime.fromISO(profile.date_of_birth).toFormat(
                    "dd/MM/yyyy",
                  )}
                </CardLine>
              )}
              <CardLine my={0}>{capitalise(profile?.gender) ?? "-"}</CardLine>
            </>
          )}
        </Card>

        {/* Address card */}
        <Card mb={2}>
          <H3 fontSize={2}>
            {isEntity ? "Registered Address" : "Residential Address"}
          </H3>

          {registeredAddress ? (
            <>
              <CardLine>{address.line1}</CardLine>
              <CardLine>{address.line2}</CardLine>
              <CardLine>{address.postcode}</CardLine>

              <Flex
                gap="8"
                alignItems="center"
                fontSize={1}
                fontFamily={"'Inter', sans-serif"}
              >
                {address.country_flag && (
                  <Box backgroundColor="gray.10" borderRadius="50%" p="1">
                    <img
                      src={`data:image/png;base64,${address.country_flag}`}
                      alt="flag"
                    />
                  </Box>
                )}
                <span>{address.country}</span>
              </Flex>
            </>
          ) : (
            <CardLine>Not provided</CardLine>
          )}
        </Card>

        {/* Screening card */}
        <Card mb={2}>
          <H3 fontSize={2}>Screening</H3>

          {selectedChecks.length ? (
            selectedChecks.map((check: any, i: number) => (
              <Flex gap="16" key={check?.id ?? i} alignItems="center">
                {checksIcons[check?.name] && (
                  <Image
                    src={checksIcons[check.name]}
                    alt="checks"
                    height="auto"
                    mr={1}
                  />
                )}
                <Paragraph my={1} fontSize={1} color="gray.60">
                  {check?.title || check?.actual_name || check?.name || "-"}
                </Paragraph>
              </Flex>
            ))
          ) : (
            <CardLine>-</CardLine>
          )}
        </Card>

        <Flex justifyContent="flex-end" mt={2}>
          <Button onClick={handleRun} isDisabled={doubleClick}>
            Run
          </Button>
        </Flex>
      </Box>

      <Modal
        isOpen={showSuccess}
        onClose={goToProfile}
        title="Screening submitted"
        content={
          "We are processing the checks on this entity. The results will be available shortly."
        }
        onModalConfirmClick={goToProfile}
        primaryButtonText="Close"
        primaryButtonVariant="primary"
        width="450px"
        hasIcon
        isSuccessful
      />
    </NewProfileScreeningFrame>
  );
};
